import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Box,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Delete, ArrowRightAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import Button from '@/components/CustomButtons/Button';
import { DataMappingsApi } from '@/api/clients/data-mappings/data-mappings.api';

const STRING_FN_OPTIONS = [
  { value: 'uppercase', labelKey: 'transformSettings.styleOptions.uppercase' },
  { value: 'lowercase', labelKey: 'transformSettings.styleOptions.lowercase' },
  {
    value: 'title_case',
    labelKey: 'transformSettings.styleOptions.titleCase',
  },
  {
    value: 'sentence_case',
    labelKey: 'transformSettings.styleOptions.sentenceCase',
  },
  {
    value: 'two_decimals',
    labelKey: 'transformSettings.styleOptions.twoDecimals',
  },
];

const EMPTY_RULE = { description: '', regex: '', replace: '' };

const CODE_STYLE = {
  background: '#eef2f7',
  padding: '2px 6px',
  borderRadius: 4,
};

const makeId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;

// U+2423 OPEN BOX renders spaces visibly inside inputs; stored value keeps real spaces.
const OPEN_BOX = '\u2423';
const toWhitespaceDisplay = (value) => (value || '').replace(/ /g, OPEN_BOX);
const fromWhitespaceDisplay = (value) =>
  value.replace(new RegExp(OPEN_BOX, 'g'), ' ');

export const DEFAULT_TRANSFORM = {
  enabled: false,
  rules: [],
  stringFn: '',
  stringInterpolation: false,
  samples: [''],
};

export default function TransformSettingsDialog({
  open,
  columnKey,
  columnValue,
  transform,
  isEditing,
  onClose,
  onChange,
}) {
  const { t } = useTranslation();
  const activeTransform = transform || DEFAULT_TRANSFORM;
  const rules = activeTransform.rules || [];
  const samples = activeTransform.samples || [''];
  const stringFn = activeTransform.stringFn ?? '';
  const stringInterpolation = activeTransform.stringInterpolation ?? false;

  // Stable React keys kept in refs so persisted rules/samples stay plain (no ids on the wire).
  const ruleIds = useRef([]);
  const sampleIds = useRef([]);
  const syncIds = (idList, length) => {
    while (idList.length < length) idList.push(makeId());
    if (idList.length > length) idList.length = length;
  };
  syncIds(ruleIds.current, rules.length);
  syncIds(sampleIds.current, samples.length);

  const [previewResults, setPreviewResults] = useState([]);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRequestId = useRef(0);
  const previewErrorFallback = t('transformSettings.previewError');

  const previewSignature = JSON.stringify({
    enabled: activeTransform.enabled,
    rules,
    stringFn,
    stringInterpolation,
    samples,
  });

  useEffect(() => {
    if (!open || !activeTransform.enabled) {
      setPreviewResults([]);
      setPreviewError(null);
      setPreviewLoading(false);
      return undefined;
    }
    setPreviewLoading(true);
    const requestId = ++previewRequestId.current;
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const { data } = await DataMappingsApi.previewTransforms(
          {
            samples: samples.map((sample) => sample ?? ''),
            columnTransform: {
              enabled: activeTransform.enabled,
              rules: rules.map((rule) => ({
                description: rule.description,
                regex: rule.regex,
                replace: rule.replace,
              })),
              stringFn,
              stringInterpolation,
            },
          },
          { signal: controller.signal },
        );
        if (requestId !== previewRequestId.current) return;
        setPreviewResults(data?.results || []);
        setPreviewError(null);
      } catch (err) {
        if (controller.signal.aborted || requestId !== previewRequestId.current)
          return;
        setPreviewError(err?.response?.data?.message || previewErrorFallback);
        setPreviewResults([]);
      } finally {
        if (requestId === previewRequestId.current) setPreviewLoading(false);
      }
    }, 500);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [open, previewSignature, previewErrorFallback]);

  const addRule = () => {
    ruleIds.current.push(makeId());
    onChange({ rules: [...rules, { ...EMPTY_RULE }] });
  };

  const removeRule = (index) => {
    ruleIds.current.splice(index, 1);
    onChange({ rules: rules.filter((_, i) => i !== index) });
  };

  const updateRule = (index, patch) =>
    onChange({
      rules: rules.map((rule, i) =>
        i === index ? { ...rule, ...patch } : rule,
      ),
    });

  const addSample = () => {
    sampleIds.current.push(makeId());
    onChange({ samples: [...samples, ''] });
  };

  const removeSample = (index) => {
    sampleIds.current.splice(index, 1);
    onChange({ samples: samples.filter((_, i) => i !== index) });
  };

  const updateSample = (index, value) =>
    onChange({ samples: samples.map((s, i) => (i === index ? value : s)) });

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {t('transformSettings.title')}
        {columnKey && (
          <>
            <Typography variant='body2' color='textSecondary'>
              {t('transformSettings.column')}:{' '}
              <code style={CODE_STYLE}>{columnKey}</code>
            </Typography>
            <Typography
              variant='body2'
              color='textSecondary'
              sx={{ mt: 0.5, wordBreak: 'break-all' }}>
              {t('transformSettings.columnValue')}:{' '}
              <code style={CODE_STYLE}>
                {columnValue || t('transformSettings.empty')}
              </code>
            </Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {open && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  color='primary'
                  checked={!!activeTransform.enabled}
                  disabled={!isEditing}
                  onChange={(e) => onChange({ enabled: e.target.checked })}
                />
              }
              label={
                <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
                  {t('transformSettings.enable')}
                </Typography>
              }
            />

            {activeTransform.enabled && (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}>
                  <Box>
                    <Typography variant='subtitle2'>
                      {t('transformSettings.regexRules')}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {t('transformSettings.regexRulesHelp')}
                    </Typography>
                  </Box>
                  <Button
                    color='primary'
                    size='sm'
                    disabled={!isEditing}
                    onClick={addRule}>
                    {t('transformSettings.addRegex')}
                  </Button>
                </Box>

                {rules.length === 0 && (
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 3,
                      mb: 1.5,
                      borderRadius: 2,
                      borderStyle: 'dashed',
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}>
                    <Typography variant='subtitle2' gutterBottom>
                      {t('transformSettings.noRegexTitle')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('transformSettings.noRegexDescription')}
                    </Typography>
                  </Paper>
                )}

                {rules.map((rule, index) => (
                  <Paper
                    key={ruleIds.current[index] ?? index}
                    variant='outlined'
                    sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <Typography
                        variant='subtitle2'
                        color='primary'
                        sx={{ fontWeight: 600 }}>
                        {t('transformSettings.regexNumber', {
                          number: index + 1,
                        })}
                      </Typography>
                      <Tooltip title={t('transformSettings.deleteRegex')}>
                        <span>
                          <IconButton
                            size='small'
                            color='error'
                            disabled={!isEditing}
                            onClick={() => removeRule(index)}>
                            <Delete fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <TextField
                      label={t('transformSettings.description')}
                      fullWidth
                      variant='standard'
                      size='small'
                      value={rule.description || ''}
                      disabled={!isEditing}
                      onChange={(e) =>
                        updateRule(index, { description: e.target.value })
                      }
                      sx={{ mt: 0.5 }}
                    />
                    <TextField
                      label={t('transformSettings.regex')}
                      fullWidth
                      variant='standard'
                      size='small'
                      value={toWhitespaceDisplay(rule.regex)}
                      disabled={!isEditing}
                      onChange={(e) =>
                        updateRule(index, {
                          regex: fromWhitespaceDisplay(e.target.value),
                        })
                      }
                      inputProps={{ style: { fontFamily: 'monospace' } }}
                      helperText={t('transformSettings.regexHelper')}
                      sx={{ mt: 1 }}
                    />
                    <TextField
                      label={t('transformSettings.replace')}
                      fullWidth
                      variant='standard'
                      size='small'
                      value={toWhitespaceDisplay(rule.replace)}
                      disabled={!isEditing}
                      onChange={(e) =>
                        updateRule(index, {
                          replace: fromWhitespaceDisplay(e.target.value),
                        })
                      }
                      inputProps={{ style: { fontFamily: 'monospace' } }}
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                ))}

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: '#f5f7fa',
                  }}>
                  <Typography variant='subtitle2' gutterBottom>
                    {t('transformSettings.styles')}
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    {t('transformSettings.stylesHelp')}
                  </Typography>
                  <FormControl
                    variant='standard'
                    fullWidth
                    sx={{ mt: 1 }}
                    disabled={!isEditing}>
                    <InputLabel id={`string-function-${columnKey}`}>
                      {t('transformSettings.style')}
                    </InputLabel>
                    <Select
                      labelId={`string-function-${columnKey}`}
                      value={activeTransform.stringFn ?? ''}
                      onChange={(e) => onChange({ stringFn: e.target.value })}>
                      <MenuItem value=''>
                        <em>{t('transformSettings.none')}</em>
                      </MenuItem>
                      {STRING_FN_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: '#f5f7fa',
                  }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color='primary'
                        checked={stringInterpolation}
                        disabled={!isEditing}
                        onChange={(e) =>
                          onChange({ stringInterpolation: e.target.checked })
                        }
                      />
                    }
                    label={t('transformSettings.stringInterpolation')}
                  />
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    component='div'>
                    {t('transformSettings.stringInterpolationHelp')}{' '}
                    <code>{'{{Column Name}}'}</code>
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#fff',
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='subtitle2'>
                        {t('transformSettings.preview')}
                      </Typography>
                      {previewLoading && <CircularProgress size={16} />}
                    </Box>
                    <Button
                      color='primary'
                      size='sm'
                      disabled={!isEditing}
                      onClick={addSample}>
                      {t('transformSettings.addSample')}
                    </Button>
                  </Box>

                  {previewError && (
                    <Typography variant='body2' color='error' sx={{ mb: 1.5 }}>
                      {previewError}
                    </Typography>
                  )}

                  {samples.map((sample, index) => (
                    <Box key={sampleIds.current[index] ?? index} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}>
                        <TextField
                          label={t('transformSettings.sampleNumber', {
                            number: index + 1,
                          })}
                          fullWidth
                          variant='outlined'
                          size='small'
                          value={sample ?? ''}
                          disabled={!isEditing}
                          onChange={(e) => updateSample(index, e.target.value)}
                        />
                        <Tooltip title={t('transformSettings.deleteSample')}>
                          <span>
                            <IconButton
                              size='small'
                              color='error'
                              disabled={!isEditing || samples.length === 1}
                              onClick={() => removeSample(index)}>
                              <Delete fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          mt: 1,
                          flexWrap: 'wrap',
                        }}>
                        <Box
                          component='span'
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 14,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: '#f5f7fa',
                            color: 'text.secondary',
                          }}>
                          {sample || t('transformSettings.empty')}
                        </Box>
                        <ArrowRightAlt color='action' />
                        <Box
                          component='span'
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 14,
                            fontWeight: 600,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: previewError
                              ? '#e0e0e0'
                              : 'primary.main',
                            color: previewError ? 'text.secondary' : '#fff',
                          }}>
                          {previewError
                            ? '—'
                            : previewResults[index]?.output ||
                              t('transformSettings.empty')}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          {t('transformSettings.done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
